import { createTheme } from "@mui/material/styles";
import {
  componentesInteracaoGlobais,
  componentesTipografiaAdmin,
  componentesTipografiaEscola,
  componentesTipografiaSre,
  tipografiaGlobal,
} from "./global";

// Os fallbacks nos estilos conservam as cores originais do tema claro.
// Toda a aparência escura é definida aqui, inclusive estilos fora do MUI.
export const coresEscuras = {
  fundo: "#0b1220",
  superficie: "#111a2b",
  "superficie-suave": "#131e30",
  "superficie-azul": "#15263c",
  "superficie-hover": "#1a2c43",
  borda: "#22304a",
  "borda-forte": "#34475f",
  cabecalho: "#111a2b",
  texto: "#ffffff",
  "texto-secundario": "#ffffff",
  "texto-suave": "#ffffff",
  azul: "#ffffff",
  "azul-forte": "#ffffff",
  "acao-azul": "#2f6db0",
  "acao-azul-hover": "#265e9a",
  "acao-sucesso": "#287852",
  "sucesso-fundo": "#173f37",
  "sucesso-texto": "#8ce0b7",
  "sucesso-borda": "#39765d",
  "erro-fundo": "#482c37",
  "erro-texto": "#ffabb4",
  "erro-borda": "#92505d",
  "aviso-fundo": "#493d24",
  "aviso-texto": "#ffd588",
  "aviso-borda": "#876b38",
  "roxo-fundo": "#343254",
  "roxo-texto": "#c7b9ff",
};

const tipografiaPorPerfil = {
  admin: componentesTipografiaAdmin,
  superintendent: componentesTipografiaSre,
  analyst: componentesTipografiaSre,
  user: componentesTipografiaEscola,
};
const temas = new Map();
const acoesEscuras = { primary: coresEscuras["acao-azul"], secondary: "#6d4aa8", success: "#287852", error: "#be3b4c", warning: "#90661c", info: coresEscuras["acao-azul"] };

export function obterTemaSear(modo = "claro", perfil) {
  const chave = `${modo}:${perfil || "publico"}`;
  if (temas.has(chave)) return temas.get(chave);
  const escuro = modo === "escuro";
  const variaveis = Object.fromEntries(Object.entries(coresEscuras).map(([nome, cor]) => [
    `--sear-${nome}`, escuro ? cor : "initial",
  ]));
  const tema = createTheme({
    typography: tipografiaGlobal,
    palette: {
      mode: escuro ? "dark" : "light",
      primary: { main: escuro ? coresEscuras.azul : perfil ? "#1976d2" : "#2196f3" },
      secondary: { main: escuro ? "#ffffff" : "#f50057" },
      ...(escuro ? {
        background: { default: coresEscuras.fundo, paper: coresEscuras.superficie },
        text: { primary: coresEscuras.texto, secondary: coresEscuras["texto-secundario"], disabled: "#ffffff" },
        divider: coresEscuras.borda,
        action: { hover: "rgba(47,109,176,.12)", selected: "rgba(47,109,176,.20)", disabled: "#ffffff", disabledBackground: "#1a2638" },
        success: { main: "#81c995" },
        error: { main: "#ffabb4" },
        warning: { main: "#ffd588" },
        info: { main: "#90caf9" },
        grey: { 50: coresEscuras.superficie, 100: coresEscuras["superficie-suave"], 200: coresEscuras["superficie-hover"], 300: coresEscuras.borda, 400: "#62738e", 500: "#ffffff", 600: "#ffffff", 700: "#ffffff", 800: "#ffffff", 900: "#ffffff" },
      } : { background: { default: "#f5f5f5", paper: "#fff" } }),
    },
    components: componentesInteracaoGlobais,
  }, { components: tipografiaPorPerfil[perfil] || {} }, {
    components: {
      MuiCssBaseline: { styleOverrides: {
        ":root": { ...variaveis, colorScheme: escuro ? "dark" : "light" },
        body: { backgroundColor: escuro ? coresEscuras.fundo : "#fff" },
        ...(escuro && { "::selection": { backgroundColor: coresEscuras["acao-azul"], color: "#fff" } }),
        ".rdw-editor-toolbar, .rdw-dropdown-wrapper, .rdw-dropdown-optionwrapper": {
          backgroundColor: "var(--sear-superficie, #fff)",
          borderColor: "var(--sear-borda, #f1f1f1)",
          color: "var(--sear-texto, #222)",
        },
        ".rdw-option-wrapper": { backgroundColor: "#fff" },
        ".rdw-dropdownoption-highlighted, .rdw-dropdownoption-active": {
          backgroundColor: "var(--sear-superficie-hover, #f1f1f1)",
        },
        // A impressão e o conteúdo de PDFs conservam a apresentação do documento.
        "@media print": {
          ":root": Object.fromEntries(Object.keys(coresEscuras).map(nome => [`--sear-${nome}`, "initial"])),
          "body, .MuiPaper-root": { backgroundColor: "#fff", color: "#000", colorScheme: "light" },
        },
      } },
      MuiPaper: { styleOverrides: { root: { ...(escuro && { backgroundImage: "none" }) } } },
      MuiButton: { styleOverrides: {
        contained: ({ ownerState }) => escuro ? {
          color: "#fff",
          backgroundColor: acoesEscuras[ownerState.color] || coresEscuras["borda-forte"],
          "&:hover": { backgroundColor: acoesEscuras[ownerState.color] || coresEscuras["borda-forte"], filter: "brightness(1.12)" },
        } : {},
      } },
    },
  });
  temas.set(chave, tema);
  return tema;
}
